import React, { useState, useEffect, useRef } from 'react';
import './VideoCarousel.css';

interface VideoCarouselProps {
    videos: string[]; // Array of S3 video URLs
    autoplay?: boolean;
    loop?: boolean;
    interval?: number; // Time in milliseconds between video changes
    showControls?: boolean;
    showIndicators?: boolean;
}

const VideoCarousel: React.FC<VideoCarouselProps> = ({
    videos,
    autoplay = true,
    loop = true,
    interval = 8000, // 8 seconds default
    showControls = true,
    showIndicators = true,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(autoplay);
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

    useEffect(() => {
        // Initialize video refs array
        videoRefs.current = videoRefs.current.slice(0, videos.length);
    }, [videos.length]);

    // Fallback timer - only used if video doesn't fire 'ended' event
    useEffect(() => {
        if (!autoplay || !isPlaying) return;

        const timer = setInterval(() => {
            // This is a fallback - the 'ended' event handler should be the primary way to advance
            // Only advance if video is near the end (within 1 second)
            const currentVideo = videoRefs.current[currentIndex];
            if (currentVideo && currentVideo.duration) {
                const timeRemaining = currentVideo.duration - currentVideo.currentTime;
                if (timeRemaining <= 1) {
            setCurrentIndex((prevIndex) => {
                const nextIndex = prevIndex + 1;
                if (nextIndex >= videos.length) {
                    return loop ? 0 : prevIndex;
                }
                return nextIndex;
            });
                }
            }
        }, 1000); // Check every second

        return () => clearInterval(timer);
    }, [autoplay, isPlaying, currentIndex, videos.length, loop]);

    useEffect(() => {
        // Play current video and pause others
        videoRefs.current.forEach((video, index) => {
            if (video) {
                if (index === currentIndex && isPlaying) {
                    video.currentTime = 0; // Reset to start
                    video.play().catch((error) => {
                        console.error('Error playing video:', error);
                    });
                } else {
                    video.pause();
                    video.currentTime = 0; // Reset paused videos
                }
            }
        });
    }, [currentIndex, isPlaying]);

    // Handle video end event to move to next video
    useEffect(() => {
        const currentVideo = videoRefs.current[currentIndex];
        if (!currentVideo) return;

        const handleVideoEnd = () => {
            if (isPlaying && autoplay) {
                setCurrentIndex((prevIndex) => {
                    const nextIndex = prevIndex + 1;
                    if (nextIndex >= videos.length) {
                        return loop ? 0 : prevIndex;
                    }
                    return nextIndex;
                });
            }
        };

        currentVideo.addEventListener('ended', handleVideoEnd);
        return () => {
            currentVideo.removeEventListener('ended', handleVideoEnd);
        };
    }, [currentIndex, isPlaying, autoplay, videos.length, loop]);

    const handlePrevious = () => {
        setCurrentIndex((prevIndex) => {
            const newIndex = prevIndex - 1;
            return newIndex < 0 ? (loop ? videos.length - 1 : 0) : newIndex;
        });
    };

    const handleNext = () => {
        setCurrentIndex((prevIndex) => {
            const newIndex = prevIndex + 1;
            return newIndex >= videos.length ? (loop ? 0 : prevIndex) : newIndex;
        });
    };

    const handleIndicatorClick = (index: number) => {
        setCurrentIndex(index);
    };

    const togglePlayPause = () => {
        setIsPlaying(!isPlaying);
    };

    if (!videos || videos.length === 0) {
        return null;
    }

    return (
        <div className="video-carousel">
            <div className="video-carousel-container">
                {videos.map((videoUrl, index) => (
                    <div
                        key={index}
                        className={`video-slide ${index === currentIndex ? 'active' : ''}`}
                    >
                        <video
                            ref={(el) => {
                                videoRefs.current[index] = el;
                            }}
                            src={videoUrl}
                            className="carousel-video"
                            muted
                            loop={false}
                            playsInline
                            preload="metadata"
                        />
                    </div>
                ))}

                {showControls && (
                    <>
                        <button
                            className="carousel-control carousel-control-prev"
                            onClick={handlePrevious}
                            aria-label="Previous video"
                        >
                            &#8249;
                        </button>
                        <button
                            className="carousel-control carousel-control-next"
                            onClick={handleNext}
                            aria-label="Next video"
                        >
                            &#8250;
                        </button>
                        <button
                            className="carousel-control carousel-control-play-pause"
                            onClick={togglePlayPause}
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                        >
                            {isPlaying ? '⏸' : '▶'}
                        </button>
                    </>
                )}

                {showIndicators && (
                    <div className="carousel-indicators">
                        {videos.map((_, index) => (
                            <button
                                key={index}
                                className={`indicator ${index === currentIndex ? 'active' : ''}`}
                                onClick={() => handleIndicatorClick(index)}
                                aria-label={`Go to video ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VideoCarousel;

